import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useOutletContext, Link } from "react-router-dom";
import { api, b2bApi, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import { isSuperAdmin, isHrAdmin, isFlmAdmin } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { PrivacyBadge } from "@/components/common/PrivacySafe";
import { Users2, MailPlus, Users, LineChart, Building2, ArrowRight, CreditCard } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, testId, accent = "bg-white" }) {
  return (
    <div className={`rounded-3xl ${accent} shadow-card p-6`} data-testid={testId}>
      <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
      </div>
      <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">{value ?? "—"}</div>
      {sub && <div className="text-xs text-ink-500 mt-1">{sub}</div>}
    </div>
  );
}

function LinkCard({ to, title, description, icon: Icon, testId }) {
  return (
    <Link to={to} data-testid={testId} className="rounded-3xl bg-white shadow-card p-6 hover:shadow-card-hover transition hover:-translate-y-0.5 flex items-start gap-3">
      <div className="h-11 w-11 rounded-2xl bg-sage-100 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-sage-700" strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <div className="font-heading text-ink-900 text-lg">{title}</div>
        <div className="text-sm text-ink-500 mt-0.5">{description}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-ink-500 mt-2" />
    </Link>
  );
}

export default function AdminDashboard() {
  const t = useT();
  const { roles, orgName, platform, orgId } = useOutletContext();
  const license = useQuery({
    queryKey: ["b2b-license", orgId],
    queryFn: async () => (await b2bApi.get(`/b2b/billing/org/${orgId}`)).data,
    retry: false,
    enabled: !!orgId && (isSuperAdmin(roles) || isHrAdmin(roles)),
  });
  const lic = license.data?.license;

  const orgs = useQuery({
    queryKey: ["admin-orgs"],
    queryFn: async () => (await api.get("/platform/organizations")).data,
    retry: false,
    enabled: isSuperAdmin(roles),
  });
  const analytics = useQuery({
    queryKey: ["admin-analytics-org"],
    queryFn: async () => (await api.get("/platform/analytics/org")).data,
    retry: false,
  });
  const invites = useQuery({
    queryKey: ["admin-invites-outbox"],
    queryFn: async () => (await api.get("/platform/invites/outbox")).data,
    retry: false,
  });
  const teams = useQuery({
    queryKey: ["admin-teams"],
    queryFn: async () => (await api.get("/platform/teams")).data,
    retry: false,
  });
  const people = useQuery({
    queryKey: ["admin-people"],
    queryFn: async () => (await api.get("/platform/people")).data,
    retry: false,
  });

  const orgList = Array.isArray(orgs.data) ? orgs.data : orgs.data?.organizations || [];
  const teamList = Array.isArray(teams.data) ? teams.data : teams.data?.teams || teams.data?.items || [];
  const peopleList = Array.isArray(people.data) ? people.data : people.data?.people || people.data?.items || [];
  const inviteList = Array.isArray(invites.data) ? invites.data : invites.data?.invites || invites.data?.items || [];
  const stats = analytics.data || {};

  const pendingInvites = inviteList.filter((i) => (i.status || "").toLowerCase() === "pending" || (i.status || "").toLowerCase() === "sent").length;

  const roleLabel = isSuperAdmin(roles)
    ? "Super admin"
    : isHrAdmin(roles)
    ? "HR admin"
    : isFlmAdmin(roles)
    ? "Team lead"
    : "Admin";

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={roleLabel}
        title={orgName ? `${orgName}` : t("platform.title")}
        subtitle={isSuperAdmin(roles) ? "Platform overview across all organizations." : t("platform.subtitle")}
        actions={<PrivacyBadge />}
      />

      {(orgs.isError || analytics.isError) && (analytics.isError) && (
        <ErrorState message={errMsg(analytics.error)} onRetry={() => analytics.refetch()} />
      )}

      {(orgs.isLoading || analytics.isLoading || teams.isLoading) && <LoadingCard height="h-24" />}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {lic && (
          <Link to="/admin/billing" data-testid="dash-stat-licence" className="block">
            <Stat icon={CreditCard} label={t("b2b.nav.licence")} value={`${lic.seats_assigned}/${lic.seats_purchased}`}
              sub={`${t(`b2b.billing.states.${lic.status}`)} · ${lic.seats_available} ${t("b2b.billing.available").toLowerCase()}`}
              accent={lic.entitlement === "active" ? "bg-sage-100/60" : "bg-white"} />
          </Link>
        )}
        {isSuperAdmin(roles) && (
          <Stat testId="dash-stat-orgs" icon={Building2} label={t("nav.organisation")} value={orgList.length || (stats.organizations ?? "—")} />
        )}
        <Stat testId="dash-stat-people" icon={Users} label={t("nav.people")} value={peopleList.length || (stats.members ?? stats.people ?? "—")} />
        <Stat testId="dash-stat-teams" icon={Users2} label={t("nav.teams")} value={teamList.length || (stats.teams ?? "—")} />
        <Stat testId="dash-stat-invites" icon={MailPlus} label={t("nav.invites")} value={pendingInvites || (stats.pending_invites ?? "—")} sub={t("common.status") + ": " + (pendingInvites ? "pending" : "quiet")} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <LinkCard testId="dash-link-teams" to="/admin/teams" title={t("nav.teams")} description="Create teams and manage members." icon={Users2} />
        <LinkCard testId="dash-link-invitations" to="/admin/invitations" title={t("nav.invites")} description="Invite employees and track status." icon={MailPlus} />
        <LinkCard testId="dash-link-analytics" to="/admin/analytics" title={t("nav.analytics")} description="Privacy-safe engagement and adoption." icon={LineChart} />
        {isSuperAdmin(roles) && (
          <LinkCard testId="dash-link-organizations" to="/admin/organizations" title={t("nav.organisation")} description="Create and manage organizations." icon={Building2} />
        )}
        <LinkCard testId="dash-link-users" to="/admin/users" title={t("nav.users")} description="Manage roles and access." icon={Users} />
      </div>
    </div>
  );
}
